import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import CheckIcon from '@mui/icons-material/Check';

const MODE_ORDER = ['system', 'light', 'dark'];

const modeMeta = {
  system: {
    label: 'Sistema',
    description: 'Segue preferência do dispositivo',
    icon: SettingsBrightnessOutlinedIcon,
  },
  light: {
    label: 'Claro',
    description: 'Tema claro',
    icon: LightModeOutlinedIcon,
  },
  dark: {
    label: 'Escuro',
    description: 'Tema escuro',
    icon: DarkModeOutlinedIcon,
  },
};

const ThemeModeToggle = ({ modeSetting, resolvedMode, onModeSettingChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const currentModeMeta = useMemo(
    () => modeMeta[modeSetting] || modeMeta.system,
    [modeSetting]
  );

  const CurrentIcon = currentModeMeta.icon;
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip
        title={`Tema: ${currentModeMeta.label}${modeSetting === 'system' ? ` (${resolvedMode})` : ''}`}
      >
        <IconButton
          color="inherit"
          aria-label="alternar tema"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            px: 1.25,
            bgcolor: 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CurrentIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            borderRadius: 2,
            p: 0.5,
          },
        }}
      >
        {MODE_ORDER.map((mode) => {
          const Icon = modeMeta[mode].icon;
          const selected = modeSetting === mode;

          return (
            <MenuItem
              key={mode}
              selected={selected}
              onClick={() => {
                onModeSettingChange(mode);
                setAnchorEl(null);
              }}
              sx={{ borderRadius: 1.5, py: 1 }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>

              <ListItemText
                primary={modeMeta[mode].label}
                secondary={modeMeta[mode].description}
              />

              {selected && <CheckIcon fontSize="small" color="primary" />}
              {mode === 'system' && (
                <Chip
                  label={resolvedMode}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1, textTransform: 'capitalize' }}
                />
              )}
            </MenuItem>
          );
        })}

        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            color: 'text.secondary',
          }}
        >
          Preferência salva neste navegador
        </Typography>
      </Menu>
    </>
  );
};

ThemeModeToggle.propTypes = {
  modeSetting: PropTypes.oneOf(['system', 'light', 'dark']).isRequired,
  resolvedMode: PropTypes.oneOf(['light', 'dark']).isRequired,
  onModeSettingChange: PropTypes.func.isRequired,
};

export default ThemeModeToggle;
